import { useState } from 'react';

import { useTranslate } from '@/hooks/useTranslate';

import { ConfirmDrawer } from '../Layout/Drawer/ConfirmDrawer';

enum RejectionType {
  ID = 'id',
  SELFIE = 'selfie',
  LIVENESS = 'liveness',
}

export interface RejectedMessageProps {
  isIDRejected: boolean;
  isSelfieRejected: boolean;
  isLivenessRejected: boolean;
  isMinor?: boolean;
  onAcknowledge?: () => void;
}

interface RejectionConfig {
  titleKey: string;
  minorTitleKey?: string;
  descriptionKey: string;
  buttonTextKey: string;
}

const REJECTION_CONFIGS: Record<RejectionType, RejectionConfig> = {
  [RejectionType.ID]: {
    titleKey: 'verification.verifyID.rejectedDrawer.title',
    minorTitleKey: 'verification.verifyID.rejectedDrawer.minor.title',
    descriptionKey: 'verification.verifyID.rejectedDrawer.description',
    buttonTextKey: 'verification.verifyID.rejectedDrawer.button.retake',
  },
  [RejectionType.SELFIE]: {
    titleKey: 'verification.verifySelfie.rejectDrawer.title',
    minorTitleKey: 'verification.verifySelfie.rejectDrawer.minor.title',
    descriptionKey: 'verification.verifySelfie.rejectDrawer.description',
    buttonTextKey: 'verification.verifySelfie.rejectDrawer.primaryText',
  },
  [RejectionType.LIVENESS]: {
    titleKey: 'verification.verifySelfie.livenessRejectDrawer.title',
    minorTitleKey: 'verification.verifySelfie.livenessRejectDrawer.minor.title',
    descriptionKey:
      'verification.verifySelfie.livenessRejectDrawer.description',
    buttonTextKey: 'verification.verifySelfie.livenessRejectDrawer.primaryText',
  },
} as const;

export function RejectedMessage({
  isIDRejected,
  isSelfieRejected,
  isLivenessRejected,
  isMinor,
  onAcknowledge,
}: RejectedMessageProps) {
  const { t } = useTranslate();
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const activeConfig = (() => {
    if (isIDRejected) return REJECTION_CONFIGS[RejectionType.ID];
    if (isSelfieRejected) return REJECTION_CONFIGS[RejectionType.SELFIE];
    if (isLivenessRejected) return REJECTION_CONFIGS[RejectionType.LIVENESS];
    return null;
  })();

  const handleAcknowledge = () => {
    setIsAcknowledged(true);
    onAcknowledge?.();
  };

  if (isAcknowledged || !activeConfig) {
    return null;
  }

  const titleKey =
    isMinor && activeConfig.minorTitleKey
      ? activeConfig.minorTitleKey
      : activeConfig.titleKey;

  return (
    <ConfirmDrawer
      title={t(titleKey)}
      description={t(activeConfig.descriptionKey)}
      icon="reject"
      iconColor={theme => theme.palette.red[500]}
      primaryAction={handleAcknowledge}
      primaryText={t(activeConfig.buttonTextKey)}
      onClose={handleAcknowledge}
    />
  );
}
